import { useRequiredContext } from "@redotech/react-util/context";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import type { ActionDefinition, ActionHandlerMap } from "./action-definition";
import { ActionId } from "./action-ids";
import { isGlobalActionId } from "./global-actions/action-ids";
import { globalActionDefinitions } from "./global-actions/definitions";
import { globalRootScope } from "./global-actions/scope-trees";
import type { ScopeNode } from "./scope-tree";

/**
 * A level in the scope navigation stack.
 */
export interface ScopeStackLevel {
  scope: ScopeNode<ActionId>;
}

/**
 * A layer in the context stack.
 * Each layer can provide context, handlers, and optionally override scope/getAction.
 */
export interface ContextStackLayer {
  /** Unique identifier for this layer (for updates/removal) */
  id: string;
  /** The entity being acted upon (e.g., conversation, order) */
  context: unknown;
  /** Action handlers for this context */
  handlers: ActionHandlerMap<ActionId>;
  /** Optional: override root scope for this layer */
  rootScope?: ScopeNode<ActionId>;
  /** Optional: override getAction for this layer */
  getAction?: (id: ActionId) => ActionDefinition<unknown, ActionId>;
}

/**
 * Context value for action scope navigation.
 *
 * There are two stacks:
 *
 * 1. **scopeStack** - Navigation within the action menu tree.
 *    When you click "Set Priority" in the command palette, it pushes the priority
 *    submenu onto scopeStack. This tracks WHERE you are in the menu hierarchy.
 *    Resets when context changes.
 *
 * 2. **contextStack** - Tracking which entity is being acted upon.
 *    When you focus a conversation row, that conversation gets pushed onto contextStack.
 *    Modals can push additional layers. This tracks WHAT you're acting on.
 *    Layers auto-pop on component unmount.
 */
export interface ActionScopeContextValue {
  // Static (set at provider mount)
  /** The default root scope */
  defaultRootScope: ScopeNode<ActionId>;
  /** The default getAction function */
  defaultGetAction: (id: ActionId) => ActionDefinition<unknown, ActionId>;

  // Scope navigation (for submenu drill-down within the action menu)
  /** Current scope (top of scope stack, or active root if empty) */
  currentScope: ScopeNode<ActionId>;
  /** The full stack of scopes (for breadcrumb display) */
  scopeStack: readonly ScopeStackLevel[];
  /** Push a scope onto the stack (drill into submenu) */
  pushScope: (scope: ScopeNode<ActionId>) => void;
  /** Pop the current scope (go back one level) */
  popScope: () => void;
  /** Reset to root scope (go back to top) */
  resetScope: () => void;
  /** Whether we're at the root scope */
  isAtRoot: boolean;

  // Context stack (for tracking which entity is focused)
  /** The full context stack */
  contextStack: readonly ContextStackLayer[];
  /** Push a context layer onto the stack */
  pushContext: (layer: ContextStackLayer) => void;
  /** Update an existing context layer by id */
  updateContext: (
    id: string,
    updates: Partial<Omit<ContextStackLayer, "id">>,
  ) => void;
  /** Remove a context layer by id */
  popContext: (id: string) => void;

  // Computed from context stack (convenience)
  /** The active context (top of context stack's context) */
  activeContext: unknown;
  /** Merged handlers from all context layers (deepest wins) */
  activeHandlers: ActionHandlerMap<ActionId>;
  /** The active root scope (deepest layer's rootScope, or default) */
  activeRootScope: ScopeNode<ActionId>;
  /** The active getAction (deepest layer's getAction, or default) */
  activeGetAction: (id: ActionId) => ActionDefinition<unknown, ActionId>;
}

const ActionScopeContext = createContext<ActionScopeContextValue | undefined>(
  undefined,
);

/**
 * Default getAction that only knows about global actions.
 * Domain-specific pages should provide their own getAction via context layers.
 */
function defaultGlobalGetAction(
  id: ActionId,
): ActionDefinition<unknown, ActionId> {
  if (isGlobalActionId(id)) {
    return globalActionDefinitions[id];
  }
  throw new Error(`Unknown action: ${id}. Is this action registered?`);
}

export interface ActionScopeProviderProps {
  children: ReactNode;
  /** The default root scope node. Defaults to global root scope. */
  rootScope?: ScopeNode<ActionId>;
  /** Default function to get an action definition by ID. Defaults to global actions only. */
  getAction?: (id: ActionId) => ActionDefinition<unknown, ActionId>;
  /** Default handlers for actions (base layer, can be overridden by context layers). */
  defaultHandlers?: ActionHandlerMap<ActionId>;
}

/**
 * Provider for action scope navigation.
 * Manages scope stack (menu navigation) and context stack (entity focus).
 */
export const ActionScopeProvider = memo(function ActionScopeProvider({
  children,
  rootScope: defaultRootScope = globalRootScope,
  getAction: defaultGetAction = defaultGlobalGetAction,
  defaultHandlers = {},
}: ActionScopeProviderProps) {
  const [scopeStack, setScopeStack] = useState<ScopeStackLevel[]>([]);
  const [contextStack, setContextStack] = useState<ContextStackLayer[]>([]);

  // Push a new context layer (or update if id already exists)
  const pushContext = useCallback((layer: ContextStackLayer) => {
    setContextStack((prev) => {
      // Remove any existing layer with same id, then add new one at end
      const filtered = prev.filter((l) => l.id !== layer.id);
      return [...filtered, layer];
    });
    // Reset scope navigation when context changes
    setScopeStack((prev) => (prev.length === 0 ? prev : []));
  }, []);

  // Update an existing context layer
  const updateContext = useCallback(
    (id: string, updates: Partial<Omit<ContextStackLayer, "id">>) => {
      setContextStack((prev) =>
        prev.map((layer) =>
          layer.id === id ? { ...layer, ...updates } : layer,
        ),
      );
    },
    [],
  );

  // Remove a context layer by id
  const popContext = useCallback((id: string) => {
    setContextStack((prev) => prev.filter((l) => l.id !== id));
    setScopeStack((prev) => (prev.length === 0 ? prev : []));
  }, []);

  // Compute active values from context stack
  const activeLayer = contextStack[contextStack.length - 1];
  const activeContext = activeLayer?.context;
  const activeRootScope = activeLayer?.rootScope ?? defaultRootScope;
  const activeGetAction = activeLayer?.getAction ?? defaultGetAction;

  // Merge handlers from all layers (deeper layers override shallower)
  // Start with defaultHandlers as the base layer
  const activeHandlers = useMemo(() => {
    const merged: ActionHandlerMap<ActionId> = { ...defaultHandlers };
    for (const layer of contextStack) {
      Object.assign(merged, layer.handlers);
    }
    return merged;
  }, [defaultHandlers, contextStack]);

  // Scope navigation uses activeRootScope as the root
  const currentScope = useMemo(
    () => scopeStack[scopeStack.length - 1]?.scope ?? activeRootScope,
    [scopeStack, activeRootScope],
  );

  const pushScope = useCallback((scope: ScopeNode<ActionId>) => {
    setScopeStack((prev) => [...prev, { scope }]);
  }, []);

  const popScope = useCallback(() => {
    setScopeStack((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      return prev.slice(0, -1);
    });
  }, []);

  const resetScope = useCallback(() => {
    setScopeStack([]);
  }, []);

  const isAtRoot = scopeStack.length === 0;

  const value = useMemo(
    (): ActionScopeContextValue => ({
      // Static
      defaultRootScope,
      defaultGetAction,
      // Scope navigation
      currentScope,
      scopeStack,
      pushScope,
      popScope,
      resetScope,
      isAtRoot,
      // Context stack
      contextStack,
      pushContext,
      updateContext,
      popContext,
      // Computed
      activeContext,
      activeHandlers,
      activeRootScope,
      activeGetAction,
    }),
    [
      defaultRootScope,
      defaultGetAction,
      currentScope,
      scopeStack,
      pushScope,
      popScope,
      resetScope,
      isAtRoot,
      contextStack,
      pushContext,
      updateContext,
      popContext,
      activeContext,
      activeHandlers,
      activeRootScope,
      activeGetAction,
    ],
  );

  return (
    <ActionScopeContext.Provider value={value}>
      {children}
    </ActionScopeContext.Provider>
  );
});

/**
 * Hook to access the action scope context.
 * Throws if not within an ActionScopeProvider.
 */
export function useActionScope(): ActionScopeContextValue {
  return useRequiredContext(ActionScopeContext);
}
